/**
 * Shared pieces of `/settings/delivery-flow` (#2454): the PUT body builder,
 * the "where does this value come from" badge, and the column / action /
 * executor label maps the sections and the board preview (#2501) share.
 *
 * #2449: the two legacy fields are required on every PUT, and each new field
 * is "omitted = unchanged". So every save sends the legacy pair as it is saved
 * on the tenant (`null` when legacy — sending the effective legacy value back
 * would promote it to a tenant value, and omitting `epochDate` would clear it)
 * plus ONLY the field(s) being edited.
 */
import { useTranslation } from 'react-i18next';
import { Badge } from '@resource-kit/ui/react';
import type { TenantSettingSource, TenantSettings, TenantSettingsBody } from '@/services/tenants';
import type { components } from '@/types/api.generated';

export type DeliveryFlow = components['schemas']['DeliveryFlow'];
type Column = DeliveryFlow['columns'][number];
type Action = DeliveryFlow['actions'][number];

export const EM_DASH = '—';

/** The PUT body: the legacy pair as saved on the tenant, then the edit. */
export function settingsBody(
  settings: TenantSettings,
  edit: Partial<TenantSettingsBody>,
): TenantSettingsBody {
  return {
    packagingCutoffTime: tenantValue(settings.packagingCutoffTime),
    epochDate: tenantValue(settings.epochDate),
    ...edit,
  };
}

/** The value saved on the tenant; `null` when the setting falls back. */
export function tenantValue<T>(setting: {
  value: T | null;
  source: TenantSettingSource;
}): T | null {
  return setting.source === 'tenant' ? setting.value : null;
}

/** Every non-tenant value falls back to the backend's default. */
export const FALLBACK_LABEL_KEY = {
  default: 'deliveryFlow.source.default',
} as const;

export function SourceBadge({
  source,
  fallback,
}: {
  source: TenantSettingSource;
  fallback: keyof typeof FALLBACK_LABEL_KEY;
}) {
  const { t } = useTranslation();
  return (
    <Badge variant={source === 'tenant' ? 'success' : 'secondary'}>
      {t(source === 'tenant' ? 'tenants.settings.source.tenant' : FALLBACK_LABEL_KEY[fallback])}
    </Badge>
  );
}

/** Column names are the board's own (`dispatch.board.stage.*`): one word per column. */
export const COLUMN_KEY = {
  awaiting_dispatch: {
    name: 'dispatch.board.stage.awaiting_dispatch',
    rule: 'deliveryFlow.columnRule.awaiting_dispatch',
  },
  awaiting_assignment: {
    name: 'dispatch.board.stage.awaiting_assignment',
    rule: 'deliveryFlow.columnRule.awaiting_assignment',
  },
  awaiting_departure: {
    name: 'dispatch.board.stage.awaiting_departure',
    rule: 'deliveryFlow.columnRule.awaiting_departure',
  },
  en_route: { name: 'dispatch.board.stage.en_route', rule: 'deliveryFlow.columnRule.en_route' },
  delivered: { name: 'dispatch.board.stage.delivered', rule: 'deliveryFlow.columnRule.delivered' },
  exception_or_cancelled: {
    name: 'dispatch.board.stage.exception_or_cancelled',
    rule: 'deliveryFlow.columnRule.exception_or_cancelled',
  },
} as const satisfies Record<Column, { name: string; rule: string }>;

export const ACTION_KEY = {
  dispatch: { name: 'deliveryFlow.action.dispatch', signal: 'deliveryFlow.actionSignal.dispatch' },
  assign_driver: {
    name: 'deliveryFlow.action.assign_driver',
    signal: 'deliveryFlow.actionSignal.assign_driver',
  },
  depart: { name: 'deliveryFlow.action.depart', signal: 'deliveryFlow.actionSignal.depart' },
  deliver: { name: 'deliveryFlow.action.deliver', signal: 'deliveryFlow.actionSignal.deliver' },
} as const satisfies Record<Action['action'], { name: string; signal: string }>;

export const EXECUTOR_KEY = {
  operator: 'deliveryFlow.executor.operator',
  onfleet: 'deliveryFlow.executor.onfleet',
  driver: 'deliveryFlow.executor.driver',
} as const satisfies Record<Action['executor'], string>;
